import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Calendar } from 'lucide-react';

export const ScrollTimeline = ({ timeline, countryName, intakes }) => {
  const { isDark } = useTheme();
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedIntake, setSelectedIntake] = useState(0);

  // If intakes provided, use multi-intake mode; otherwise use legacy single timeline
  const hasMultipleIntakes = intakes && intakes.length > 0;
  const currentTimeline = hasMultipleIntakes ? intakes[selectedIntake]?.steps : timeline;
  const currentIntakeName = hasMultipleIntakes ? intakes[selectedIntake]?.name : null;

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      if (rect.top >= windowHeight) {
        setProgress(0);
        setActiveStep(0);
        return;
      }
      
      if (rect.bottom <= 0) {
        setProgress(100);
        setActiveStep(currentTimeline?.length - 1 || 0);
        return;
      }
      
      const scrolledIntoSection = windowHeight - rect.top;
      const totalScrollRange = windowHeight + rect.height;
      const rawProgress = (scrolledIntoSection / totalScrollRange) * 100;
      const scaledProgress = rawProgress * 1.4;
      const clampedProgress = Math.max(0, Math.min(100, scaledProgress));
      setProgress(clampedProgress);
      
      const stepProgress = clampedProgress / 100;
      const newActiveStep = Math.min(
        Math.floor(stepProgress * (currentTimeline?.length || 1)),
        (currentTimeline?.length || 1) - 1
      );
      setActiveStep(newActiveStep);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentTimeline?.length, selectedIntake]);

  // Reset progress when changing intakes
  useEffect(() => {
    setProgress(0);
    setActiveStep(0);
  }, [selectedIntake]);

  if (!currentTimeline || currentTimeline.length === 0) return null;

  return (
    <section 
      ref={sectionRef}
      className={`py-20 md:py-32 relative overflow-hidden ${isDark ? 'bg-[#1a2830]' : 'bg-ux-charcoal'}`}
      data-testid="scroll-timeline"
    >
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-ux-cyan/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-ux-violet/10 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10">
          <Badge className="bg-ux-cyan/20 text-ux-cyan border-ux-cyan/30 mb-4">
            Application Timeline
          </Badge>
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-4 text-white tracking-tight">
            Your Journey to <span className="gradient-text-aurora">{countryName}</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {hasMultipleIntakes 
              ? 'Choose your intake and follow your path to success'
              : 'Follow your path to success - scroll to see each milestone'}
          </p>
        </div>

        {/* Intake Selector Tabs */}
        {hasMultipleIntakes && (
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white/5 backdrop-blur-sm rounded-2xl p-2 border border-white/10">
              {intakes.map((intake, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedIntake(index)}
                  className={`relative px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    selectedIntake === index
                      ? 'bg-gradient-to-r from-ux-cyan to-ux-violet text-white shadow-lg shadow-ux-cyan/20'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{intake.name}</span>
                  </div>
                  {selectedIntake === index && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-ux-cyan rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Current Intake Label */}
        {hasMultipleIntakes && currentIntakeName && (
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ux-cyan/10 border border-ux-cyan/30 text-ux-cyan text-sm font-medium">
              <Calendar className="w-4 h-4" />
              {currentIntakeName} Intake Timeline
            </span>
          </div>
        )}

        {/* Timeline Container */}
        <div className="relative">
          {/* Vertical Progress Line - Desktop */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2">
            <div className="absolute inset-0 bg-white/10 rounded-full" />
            <div 
              className="absolute top-0 left-0 right-0 bg-gradient-to-b from-ux-cyan via-ux-violet to-ux-coral rounded-full transition-all duration-300 ease-out"
              style={{ height: `${progress}%` }}
            />
            <div 
              className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-ux-cyan rounded-full shadow-glow-cyan transition-all duration-300"
              style={{ top: `${Math.min(progress, 98)}%` }}
            >
              <div className="absolute inset-0 bg-ux-cyan rounded-full animate-ping opacity-50" />
            </div>
          </div>

          {/* Mobile Progress Line */}
          <div className="md:hidden absolute left-6 top-0 bottom-0 w-1">
            <div className="absolute inset-0 bg-white/10 rounded-full" />
            <div 
              className="absolute top-0 left-0 right-0 bg-gradient-to-b from-ux-cyan via-ux-violet to-ux-coral rounded-full transition-all duration-300"
              style={{ height: `${progress}%` }}
            />
          </div>

          {/* Timeline Steps */}
          <div className="space-y-8 md:space-y-0">
            {currentTimeline.map((item, index) => {
              const isActive = index <= activeStep;
              const isCurrent = index === activeStep;
              const isLeft = index % 2 === 0;

              return (
                <div 
                  key={`${selectedIntake}-${index}`}
                  className={`relative flex items-start md:items-center ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  } transition-all duration-500 ${
                    isActive ? 'opacity-100' : 'opacity-40'
                  }`}
                >
                  {/* Mobile Layout */}
                  <div className="md:hidden flex items-start gap-4 pl-12 w-full">
                    <div 
                      className={`absolute left-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${
                        isActive 
                          ? 'bg-ux-cyan border-ux-cyan scale-110' 
                          : 'bg-transparent border-white/30'
                      } ${isCurrent ? 'ring-4 ring-ux-cyan/30' : ''}`}
                    >
                      {isActive && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </div>

                    <div 
                      className={`flex-1 p-5 rounded-2xl transition-all duration-500 ${
                        isActive 
                          ? 'bg-white/10 backdrop-blur-sm border border-white/20' 
                          : 'bg-white/5'
                      } ${isCurrent ? 'scale-[1.02] shadow-lg shadow-ux-cyan/10' : ''}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-sm font-bold ${isActive ? 'text-ux-cyan' : 'text-white/50'}`}>
                          Step {index + 1}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-white/10 text-white/50'
                        }`}>
                          {item.month}
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-white/60'}`}>
                        {item.activity}
                      </p>
                      {item.details && (
                        <p className={`text-xs mt-1 ${isActive ? 'text-white/70' : 'text-white/40'}`}>
                          {item.details}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className={`hidden md:flex items-center w-full ${isLeft ? '' : 'flex-row-reverse'}`}>
                    <div className={`w-5/12 ${isLeft ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                      <div 
                        className={`inline-block p-6 rounded-3xl transition-all duration-500 ${
                          isActive 
                            ? 'bg-white/10 backdrop-blur-sm border border-white/20' 
                            : 'bg-white/5 border border-transparent'
                        } ${isCurrent ? 'scale-105 shadow-xl shadow-ux-cyan/20' : 'hover:scale-[1.02]'}`}
                      >
                        <div className={`flex items-center gap-3 mb-3 ${isLeft ? 'justify-end' : 'justify-start'}`}>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                            isActive ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-white/10 text-white/50'
                          }`}>
                            {item.month}
                          </span>
                        </div>
                        <p className={`text-lg font-medium ${isActive ? 'text-white' : 'text-white/60'}`}>
                          {item.activity}
                        </p>
                        {item.details && (
                          <p className={`text-sm mt-2 ${isActive ? 'text-white/70' : 'text-white/40'}`}>
                            {item.details}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="w-2/12 flex justify-center">
                      <div 
                        className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500 ${
                          isActive 
                            ? 'bg-gradient-to-br from-ux-cyan to-ux-violet scale-110' 
                            : 'bg-white/10'
                        } ${isCurrent ? 'ring-4 ring-ux-cyan/30 shadow-glow-cyan' : ''}`}
                      >
                        <span className={`font-display font-bold text-xl ${isActive ? 'text-white' : 'text-white/40'}`}>
                          {index + 1}
                        </span>
                        {isCurrent && (
                          <div className="absolute inset-0 rounded-full border-2 border-ux-cyan animate-ping opacity-30" />
                        )}
                      </div>
                    </div>

                    <div className="w-5/12" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Completion indicator — positioned OUTSIDE the timeline container so the
            vertical progress line ends cleanly at the final step's endpoint dot
            instead of continuing through the "Journey Complete!" pill.
            Uses inline neon-green styles (rather than `text-ux-green` utility) so
            the pill keeps its bright brand look in BOTH themes — the timeline
            surface behind it is always dark, so the darker light-mode green
            variant would be hard to read here. */}
        {progress >= 95 && (
          <div className="mt-10 md:mt-14 text-center animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="journey-complete-pill">
            <div
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full"
              style={{
                backgroundColor: 'rgba(191, 246, 91, 0.18)',
                border: '1px solid rgba(191, 246, 91, 0.5)',
                boxShadow: '0 0 20px rgba(191, 246, 91, 0.18)',
              }}
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#bff65b' }} />
              <span className="font-semibold text-sm tracking-wide" style={{ color: '#bff65b' }}>
                Journey Complete!
              </span>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default ScrollTimeline;
